import { Link, useNavigate } from "react-router-dom";
import { Layers, ShieldCheck, ArrowLeft } from "lucide-react";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export const SecureBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 border border-[#16A34A]/30 bg-[#16A34A]/10 text-[#16A34A] px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${className}`}
    data-testid="secure-cloud-badge"
  >
    <ShieldCheck className="h-4 w-4" /> Secure Cloud Platform
  </span>
);

export const LegalShell = ({ title, updated = "June 2026", children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-brand">
            <div className="h-8 w-8 bg-[#0B3D91] flex items-center justify-center"><Layers className="h-5 w-5 text-white" /></div>
            <span className="text-lg font-extrabold tracking-tight" style={JK}>RecoverOS</span>
          </Link>
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0B3D91]" data-testid="legal-back">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D91]">Legal</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>{title}</h1>
        <p className="mt-2 text-sm text-slate-500">Last updated: {updated}</p>
        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-slate-700">{children}</div>
      </main>

      <footer className="bg-slate-950 text-white">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 bg-[#0B3D91] flex items-center justify-center"><Layers className="h-4 w-4 text-white" /></div>
                <span className="text-lg font-extrabold" style={JK}>RecoverOS</span>
              </div>
              <p className="mt-2 text-sm text-slate-400">A product of Trustera Services · New Delhi, India</p>
            </div>
            <SecureBadge />
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link>
            <Link to="/security" className="hover:text-white">Security</Link>
            <a href="mailto:support@recoveros.in" className="hover:text-white">Contact Us</a>
            <span className="ml-auto text-slate-500">© {new Date().getFullYear()} RecoverOS · Trustera Services</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Shared section helpers
export const H2 = ({ children }) => (
  <h2 className="text-xl font-bold text-slate-950" style={JK}>{children}</h2>
);

export const Section = ({ title, children }) => (
  <section className="space-y-3">
    <H2>{title}</H2>
    {children}
  </section>
);

export const DataOwnershipBlock = () => (
  <div className="border-l-4 border-[#16A34A] bg-[#16A34A]/5 p-5" data-testid="data-ownership-block">
    <p className="font-bold text-slate-900" style={JK}>Data Ownership</p>
    <ul className="mt-3 space-y-1.5 list-disc list-inside text-slate-700">
      <li>All customer data belongs to the tenant (the customer organization).</li>
      <li>RecoverOS does not sell tenant data.</li>
      <li>RecoverOS does not share tenant data with other tenants.</li>
      <li>RecoverOS does not disclose tenant-level performance metrics.</li>
      <li>Tenants can export their data at any time.</li>
    </ul>
  </div>
);

export const ProviderDisclaimer = () => (
  <div className="border border-slate-200 bg-slate-50 p-5">
    <p className="font-semibold text-slate-900">RecoverOS is a SaaS software provider</p>
    <p className="mt-2 text-slate-700">
      RecoverOS is <strong>not</strong> a lender, recovery agency, collection agency or financial institution.
      RecoverOS provides workflow and communication-integration software. The tenant is solely responsible for:
    </p>
    <ul className="mt-3 space-y-1.5 list-disc list-inside text-slate-700">
      <li>Obtaining all necessary customer communication consents</li>
      <li>Regulatory and legal compliance applicable to its business</li>
      <li>The content and frequency of all communications it sends</li>
    </ul>
  </div>
);
